import { useMemo } from 'react';
import { Flex, Stack, Text } from '@tonic-one/react';
import Organization from '@Components/Common/Organization';
import MenuWithLabel from '@Components/Common/Menu';
import { useTranslation } from 'react-i18next';
import { EuqTimeRangeEnum } from './type';
import { usePersistedTimeRange } from './hooks/usePersistedTimeRange';
import { isInUIC } from '@Utils/common';
import EuqChartCAS from './EuqChart/CAS';
import EuqChartCECP from './EuqChart/CECP';
import ReportedEmailDetailsCAS from './ReportedEmailDetails/CAS';
import ReportedEmailDetailsCECP from './ReportedEmailDetails/CECP';
import config from 'src/config';

const UserReportedEmails = () => {
  const { timeRange, setTimeRange } = usePersistedTimeRange(
    EuqTimeRangeEnum.Last1w
  );

  const isCECP = useMemo(() => {
    if (
      config.ui.deployEnv === 'dev' ||
      config.ui.deployEnv === 'beta' ||
      import.meta.env.DEV
    ) {
      return true;
    }
    return isInUIC();
  }, []);

  return (
    <Flex direction="column" flex="auto" position="relative">
      <HeaderPart timeRange={timeRange} setTimeRange={setTimeRange} />
      {isCECP ? (
        <>
          <EuqChartCECP timeRange={timeRange} />
          <ReportedEmailDetailsCECP timeRange={timeRange} />
        </>
      ) : (
        <>
          <EuqChartCAS timeRange={timeRange} />
          <ReportedEmailDetailsCAS timeRange={timeRange} />
        </>
      )}
    </Flex>
  );
};

export default UserReportedEmails;

const HeaderPart = ({
  timeRange,
  setTimeRange,
}: {
  timeRange: EuqTimeRangeEnum;
  setTimeRange: (timeRange: EuqTimeRangeEnum) => void;
}) => {
  const { t } = useTranslation(['operations', 'common']);

  const timeRangeMenuItems = [
    { key: EuqTimeRangeEnum.Last24h, value: 'common.timeRange.last24h' },
    { key: EuqTimeRangeEnum.Last1w, value: 'common.timeRange.last1w' },
    { key: EuqTimeRangeEnum.Last1M, value: 'common.timeRange.last1M' },
    { key: EuqTimeRangeEnum.Last3M, value: 'common.timeRange.last3M' },
  ].map((item) => {
    return {
      key: item.key,
      value: t(item.value),
    };
  });

  return (
    <Stack p="4x" gap="4x">
      <Text>{t('operations.tab.euq.desc')}</Text>
      <Flex columnGap="4x">
        <Organization />
        <MenuWithLabel
          menuItems={timeRangeMenuItems}
          minWidth={250}
          onClick={(e: any) => {
            setTimeRange(e.target.value as EuqTimeRangeEnum);
          }}
          selectedKey={timeRange}
          label={t('common.label.timeRange')}
        />
      </Flex>
    </Stack>
  );
};
